import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, History, FileSignature, Loader2 } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';
import PageHeader from '../Shared/PageHeader';
import { Avatar, EmptyState } from '../ui';
import {
  SUBMIT_META,
  CURRENT_STATUS_META,
  VERSION_TOOLTIP,
  fmt,
  deciderOf,
  prepareCycles,
} from './reviewHistoryShared';

/**
 * Full dedicated audit page for the review/attestation history of one
 * reviewable entity (FECOA-1611). This is the deep view behind the at-a-glance
 * modal: every submit-to-decision cycle rendered as a vertical timeline with
 * the complete record — decision reason and the full 21 CFR Part 11
 * e-signature block (meaning / signed-by / at).
 *
 * Route: /review-history/:entityType/:objectId
 * `entityName` and `currentStatus` arrive via router state when navigated from
 * the modal; the page still renders (without the header chrome) on a cold load.
 */

// One circular node sitting on the timeline rail, with its badge + body.
const TimelineNode = ({ meta, badgeExtra, topMargin, children }) => {
  const Icon = meta.icon;
  return (
    <li className={`ml-5 ${topMargin}`}>
      {/* Opaque base so the rail does not show through the tinted node */}
      <span className="absolute -left-3 flex h-6 w-6 rounded-full bg-white dark:bg-gray-800">
        <span className={`flex h-6 w-6 items-center justify-center rounded-full ring-1 ring-inset ${meta.cls}`}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      </span>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className={`badge ${meta.cls}`}>
          {meta.label}
        </span>
        {badgeExtra}
      </div>
      {children}
    </li>
  );
};

const versionBadge = (version) => {
  if (version.kind === 'none') return null;
  if (version.kind === 'unchanged') {
    return (
      <span className="text-[12.5px] text-gray-400 dark:text-gray-500" title={VERSION_TOOLTIP}>
        Unchanged since last submission
      </span>
    );
  }
  return (
    <span className="inline-flex cursor-help items-center gap-1.5" title={VERSION_TOOLTIP}>
      <span className={`text-[12.5px] ${version.kind === 'reviewed' ? 'text-gray-500 dark:text-gray-400' : 'text-warning-700 dark:text-warning-400'}`}>
        {version.kind === 'reviewed' ? 'Reviewed version' : 'Content changed'}
      </span>
      <span className="code-chip">
        {version.hash}
      </span>
    </span>
  );
};

const ReviewHistoryPage = () => {
  const { entityType = 'assessment', objectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { entityName, currentStatus } = location.state || {};

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getReviewHistory(entityType, objectId);
        if (active) setRows(Array.isArray(data) ? data : data.results || []);
      } catch {
        if (active) setError('Could not load review history.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [entityType, objectId]);

  // Newest cycle first so the latest activity is visible without scrolling (FECOA-1599).
  const cycles = prepareCycles(rows).reverse();
  const statusMeta = currentStatus ? CURRENT_STATUS_META[currentStatus] : null;

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="back-link"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        <span>Back</span>
      </button>

      {/* Header */}
      <PageHeader
        icon={History}
        title="Audit history"
        subtitle={entityName || 'Complete review and approval record'}
        titleAccessory={
          statusMeta ? (
            <span className={`badge badge-dot ${statusMeta.cls}`}>
              {statusMeta.label}
            </span>
          ) : null
        }
      />

      {/* States */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Loading audit history…
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400">{error}</div>
      )}
      {!loading && !error && cycles.length === 0 && (
        <div className="card">
          <EmptyState icon={History} title="No review activity yet for this item." />
        </div>
      )}

      {/* Timeline */}
      {!loading && cycles.length > 0 && (
        <div className="card px-6 py-6">
          <ol className="relative ml-3 border-l border-gray-200 dark:border-white/10">
            {cycles.map(({ r, decided, decisionMeta, version, standalone }, idx) => {
              const decider = deciderOf(r);
              const cycleStart = idx === 0 ? 'mt-1' : 'mt-7';
              // Standalone actions (publish, revoke) render as a single node with
              // no preceding "Submitted for review" node (FECOA-1694).
              if (standalone) {
                return (
                  <TimelineNode key={r.id} meta={decisionMeta} topMargin={cycleStart}>
                    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13.5px] text-gray-700 dark:text-gray-300">
                      by <Avatar name={decider.name} size="xs" /> <span className="font-medium text-gray-900 dark:text-gray-50">{decider.name}</span>
                      {decider.email && decider.email !== decider.name && (
                        <span className="text-gray-500 dark:text-gray-400"> ({decider.email})</span>
                      )}
                      <span className="tabular-nums text-gray-500 dark:text-gray-400"> · {fmt(r.decided_at || r.submitted_at)}</span>
                    </p>
                    {r.decision_reason && (
                      <p className="mt-2 rounded-lg border border-gray-200/70 bg-gray-50 px-3.5 py-2.5 text-[13px] leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300">
                        “{r.decision_reason}”
                      </p>
                    )}
                    {r.signed_by_name && (
                      <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[12.5px] text-gray-500 dark:text-gray-400">
                        <FileSignature className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                        {r.signature_meaning || 'Signed'}, {r.signed_by_name}
                        {r.signed_by_email && r.signed_by_email !== r.signed_by_name && (
                          <span className="text-gray-500 dark:text-gray-400"> ({r.signed_by_email})</span>
                        )}
                        <span className="tabular-nums text-gray-500 dark:text-gray-400"> · {fmt(r.signed_at)}</span>
                      </p>
                    )}
                  </TimelineNode>
                );
              }
              return (
                <React.Fragment key={r.id}>
                  {/* Decision node first — the outcome (or awaiting placeholder) is the
                      newest event in the cycle, so it sits on top (FECOA-1599) */}
                  <TimelineNode meta={decisionMeta} topMargin={cycleStart}>
                    {decided ? (
                      <>
                        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13.5px] text-gray-700 dark:text-gray-300">
                          by <Avatar name={decider.name} size="xs" /> <span className="font-medium text-gray-900 dark:text-gray-50">{decider.name}</span>
                          {decider.email && decider.email !== decider.name && (
                            <span className="text-gray-500 dark:text-gray-400"> ({decider.email})</span>
                          )}
                          <span className="tabular-nums text-gray-500 dark:text-gray-400"> · {fmt(r.decided_at)}</span>
                        </p>
                        {r.decision_reason && (
                          <p className="mt-2 rounded-lg border border-gray-200/70 bg-gray-50 px-3.5 py-2.5 text-[13px] leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300">
                            “{r.decision_reason}”
                          </p>
                        )}
                        {r.signed_by_name && (
                          <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[12.5px] text-gray-500 dark:text-gray-400">
                            <FileSignature className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                            {r.signature_meaning || 'Signed'}, {r.signed_by_name}
                            {r.signed_by_email && r.signed_by_email !== r.signed_by_name && (
                              <span className="text-gray-500 dark:text-gray-400"> ({r.signed_by_email})</span>
                            )}
                            <span className="tabular-nums text-gray-500 dark:text-gray-400"> · {fmt(r.signed_at)}</span>
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[13px] text-gray-500 dark:text-gray-400">
                        Waiting for an approver to review this submission.
                      </p>
                    )}
                  </TimelineNode>

                  {/* Submitted node — always present, beneath its decision */}
                  <TimelineNode
                    meta={SUBMIT_META}
                    badgeExtra={versionBadge(version)}
                    topMargin="mt-4"
                  >
                    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13.5px] text-gray-700 dark:text-gray-300">
                      by <Avatar name={r.submitted_by_name || 'Unknown'} size="xs" /> <span className="font-medium text-gray-900 dark:text-gray-50">{r.submitted_by_name || 'Unknown'}</span>
                      {r.submitted_by_email && r.submitted_by_email !== r.submitted_by_name && (
                        <span className="text-gray-500 dark:text-gray-400"> ({r.submitted_by_email})</span>
                      )}
                      <span className="tabular-nums text-gray-500 dark:text-gray-400"> · {fmt(r.submitted_at)}</span>
                    </p>
                  </TimelineNode>
                </React.Fragment>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
};

export default ReviewHistoryPage;
